import { AnimatedItem } from '@libertify/ui';
import { Flex } from 'antd';
import { Suggestion } from './Suggestion';
import { MediaAnalyticsEvents } from '../../types';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useChatSessionId } from '../../hooks/useChatSessionId';
import { useAntTheme } from '../../hooks/useAntTheme';
import { useTranslation } from 'react-i18next';
import { AwesomeDuoIcon } from '../icons';
import { useMemo } from 'react';
import { ProcessingLabel } from './components/ProcessingLabel';

const StarIcon = ({ color }: { color: string }) => {
  return (
    <span style={{ position: 'relative', top: 5, left: 2 }}>
      <AwesomeDuoIcon stroke={color} width={20} height={20} />
    </span>
  );
};

type FollowUpQuestionsProps = {
  suggestedQuestions?: string[];
  isProcessing: boolean;
  sendQuery: (text: string) => void;
  isFinal: boolean;
};

export const FollowUpQuestions = ({
  suggestedQuestions,
  isProcessing,
  sendQuery,
  isFinal,
}: FollowUpQuestionsProps) => {
  const { trackEvent } = useAnalytics();
  const { theme } = useAntTheme();
  const { t } = useTranslation('translation', { useSuspense: false });
  const getChatSessionId = useChatSessionId();
  const containsSuggestions = !!suggestedQuestions?.length;
  const processedSuggestions = isFinal && containsSuggestions;
  const hasSuggestions = processedSuggestions && containsSuggestions;

  const handleOnSelect = (text: string) => {
    // keep this event for backward compatibility
    // but add a new property to identify the new use case
    // with missing ChatMessageSend
    trackEvent(MediaAnalyticsEvents.ChatFollowUpClick, {
      query_source: 'suggested',
      chat_session_id: getChatSessionId(),
    });
    trackEvent(MediaAnalyticsEvents.ChatMessageSend, {
      input_position: 'chat',
      query_source: 'suggested',
      chat_session_id: getChatSessionId(),
    });
    return sendQuery(text);
  };

  const label = useMemo(() => {
    if (isProcessing) {
      return t('processingFollowUpQuestions');
    }
    if (hasSuggestions) {
      return t('followUpQuestions');
    }
    return undefined;
  }, [isProcessing, hasSuggestions, t]);

  return (
    <div>
      {/* When PROCESSING/ PROCESSED SUGGESTIONS */}
      {(isProcessing || hasSuggestions) && (
        <ProcessingLabel
          icon={<StarIcon color={theme.colorText} />}
          label={label}
          loading={isProcessing}
        />
      )}
      {/* When CONTAINS SUGGESTIONS */}
      {containsSuggestions && (
        <Flex
          vertical
          gap={8}
          style={{
            margin: 10,
          }}
        >
          {suggestedQuestions?.map((suggestion, index) => {
            return (
              <AnimatedItem key={suggestion} $delay={index * 100}>
                <Suggestion question={suggestion} onSelect={handleOnSelect} />
              </AnimatedItem>
            );
          })}
        </Flex>
      )}
    </div>
  );
};
